import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseSelectViewModel, UpdateFloorRequest } from '@core/api';
import { RoutePath } from '@core/routing/routing.types';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { EditStageService } from '@core/services/edit-stage/edit-stage.service';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OverrideModule } from '@override/override.module';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { SafeHtmlPipe } from '../../../../app/shared/pipes/safe-html.pipe';

@Component({
  selector: 'app-edit-stage',
  imports: [
    CommonModule,
    TranslateModule,
    SmartusComponentsModule,
    FormlyModule,
    OverrideModule,
    SafeHtmlPipe,
  ],
  templateUrl: './edit-stage.component.html',
  styleUrl: './edit-stage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditStageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private breadcrumbsService = inject(BreadcrumbService);
  private translateService = inject(TranslateService);
  private editStageService = inject(EditStageService);
  private cd = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private toastr = inject(ToastrService);
  private router = inject(Router);

  formModel: any = {
    floorId: '',
    floorDescription: '',
    floorName: '',
    changeReason: '',
  };
  form: FormGroup;
  formFields: FormlyFieldConfig[];
  changeReasons: BaseSelectViewModel[] = [];

  stage: any;

  private initialFloorDescription: string;
  isLoading: boolean = false;

  stageAddress = '';

  frozenZoneStartDate = '';
  frozenZoneEndDate = '';
  frozenStatus: boolean = false;
  description = '';
  descriptionStyle =
    'background-color: #E20074; color: white; border-radius: 5px; padding: 15px;';
  descriptionContainerStyle =
    'width: 100%; max-width: 840px; border-radius: 15px; padding: 0 15px;';

  cardFields: CardConfig = {
    footer: {
      button2: {
        label: this.translateService.instant('edit_stage.labels.cancel'),
        onClick: () => {
          if (window.opener || window.history.length === 1) {
            window.close();
          } else {
            this.router.navigateByUrl(`/${RoutePath.DATA_MAINTENANCE}`);
          }
        },
        disabled: false,
        type: 'button',
      },
      button1: {
        label: this.translateService.instant('edit_stage.labels.save'),
        onClick: () => {
          this.updateDesignationSave();
        },
        disabled: true,
        type: 'submit',
      },
    },
  };

  @Input() stageId: string;
  @Input() isEmbedded: boolean = false;
  @Output() closeEmbeddedView = new EventEmitter<void>();
  @Output() componentLoaded = new EventEmitter<void>();
  @Output() stageUpdated = new EventEmitter<void>();

  public ngOnInit(): void {
    if (!this.isEmbedded) {
      this.breadcrumbsService.setBreadcrumbs([
        {
          label: this.translateService.instant('edit_stage.label__title'),
        },
      ]);
    }

    const stageDataSource = this.isEmbedded
      ? this.loadStageDataForEmbedded()
      : this.loadStageDataFromRoute();

    stageDataSource
      .pipe(
        switchMap(stageData => {
          if (stageData) {
            this.stage = stageData.stage || stageData;
            this.stageId = stageData.stageId || this.stageId;

            this.initializeStageFormData(this.stage);
          }

          return forkJoin({
            frozenZoneStatus: this.editStageService
              .checkFrozenZone()
              .pipe(catchError(() => of({ startDate: '', endDate: '' }))),
            changeReasons: this.editStageService.getChangeReasons(),
          });
        }),
        finalize(() => {
          this.isLoading = false;
          if (this.isEmbedded) {
            setTimeout(() => {
              this.componentLoaded.emit();
            }, 100);
          }
        })
      )
      .subscribe({
        next: results => {
          this.changeReasons = results.changeReasons;

          const frozenZoneData = results.frozenZoneStatus;
          if (
            frozenZoneData &&
            frozenZoneData.startDate?.length > 0 &&
            frozenZoneData.endDate?.length > 0
          ) {
            this.frozenZoneStartDate = frozenZoneData.startDate;
            this.frozenZoneEndDate = frozenZoneData.endDate;
            this.frozenStatus = true;
            this.description = this.translateService.instant(
              'data_maintenance.frozenZone',
              {
                frozenZoneStartDate: this.frozenZoneStartDate,
                frozenZoneEndDate: this.frozenZoneEndDate,
              }
            );
          } else {
            this.frozenStatus = false;
          }

          this.formFields = this.getFormFields();

          if (this.form && this.frozenStatus) {
            this.disableFormFields();
          }

          this.cd.detectChanges();
        },
        error: error => {
          console.error('Error loading stage data:', error);
          this.isLoading = false;
          if (this.isEmbedded) {
            this.componentLoaded.emit();
          }
        },
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.cd.detectChanges();
    }, 0);
  }

  private loadStageDataForEmbedded(): Observable<any> {
    if (!this.stageId) {
      return of(null);
    }

    this.isLoading = true;
    return this.editStageService.getBuildingMaintenanceInformation(
      this.stageId
    );
  }

  private loadStageDataFromRoute(): Observable<any> {
    return this.route.data.pipe(map(data => data.stage));
  }

  private initializeStageFormData(stage: any): void {
    if (!stage) return;

    this.stageAddress =
      (stage.street || '--') +
      (stage.postalCode || stage.city ? ', ' : '') +
      ((stage.postalCode || '') + (stage.city ? ' ' + stage.city : ''));

    this.formModel = stage.buildingFloor;
    this.formModel.changeReason = '';
    this.initializeForm();
    this.initialFloorDescription = this.formModel?.floorDescription || '';

    if (this.frozenStatus) {
      this.disableFormFields();
    }
  }

  initializeForm(): void {
    this.form = this.fb.group({
      floorId: [this.formModel?.floorId || '--'],
      floorDescription: [this.formModel?.floorDescription || '--'],
      floorName: [this.formModel?.floorName || '--'],
      changeReason: [''],
    });

    this.form.get('floorDescription')?.valueChanges.subscribe(() => {
      this.checkFormValidity();
    });

    this.form.get('changeReason')?.valueChanges.subscribe(() => {
      this.checkFormValidity();
    });
  }

  private getFormFields(): FormlyFieldConfig[] {
    return [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            key: 'floorId',
            type: 'input',
            wrappers: ['readOnly'],
            props: {
              label: this.translateService.instant('edit_stage.labels.floorId'),
            },
          },
          {
            key: 'floorDescription',
            type: 'input',
            props: {
              label: this.translateService.instant(
                'edit_stage.labels.floorDescription'
              ),
              required: true,
            },
            className: 'margin-to-top',
          },
          {
            key: 'changeReason',
            type: 'input-search',
            props: {
              label: this.translate('buildingMaintenance.table.changeReason'),
              required: true,
              options: this.changeReasons.map(
                (changeReason: BaseSelectViewModel) => ({
                  value: changeReason.id,
                  label: changeReason.name,
                })
              ),
            },
          },
        ],
      },
    ];
  }

  private disableFormFields(): void {
    if (this.frozenStatus && this.form) {
      Object.keys(this.form.controls).forEach(key => {
        if (key !== 'changeReason') {
          this.form.get(key)?.disable();
        }
      });
    }
  }

  checkFormValidity(): void {
    const floorDescriptionChanged =
      this.form.get('floorDescription')?.value !== this.initialFloorDescription;
    const changeReasonSelected = !!this.form.get('changeReason')?.value;

    this.updateButtonDisabledState(
      !(floorDescriptionChanged && changeReasonSelected)
    );
  }

  updateButtonDisabledState(isDisabled: boolean) {
    this.cardFields.footer.button1.disabled = isDisabled;
  }

  updateDesignationSave(): void {
    if (this.form.valid) {
      this.isLoading = true;
      const updatedModel: UpdateFloorRequest = {
        id: this.formModel.floorId,
        name2: this.form.value.floorDescription,
        changeReason: this.form.value.changeReason,
      };

      this.editStageService.updateFloor(updatedModel).subscribe({
        next: () => {
          this.isLoading = false;
          this.toastr.success(
            this.translateService.instant('edit_stage.toastr.success')
          );
          this.formModel.changeReason = '';
          const changeReasonControl = this.form.get('changeReason');

          if (changeReasonControl) {
            changeReasonControl.setValue('', { emitEvent: false });
            changeReasonControl.markAsPristine();
            changeReasonControl.markAsUntouched();
          }
          this.updateButtonDisabledState(true);

          if (this.isEmbedded) {
            this.stageUpdated.emit();
          }

          this.cd.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.cd.detectChanges();
          this.toastr.error(
            this.translateService.instant('edit_stage.toastr.error')
          );
        },
      });
    }
  }

  translate(key: string): string {
    return this.translateService.instant(key);
  }

  get adaptedCardFields(): CardConfig {
    if (this.isEmbedded) {
      return {
        ...this.cardFields,
        footer: {
          ...this.cardFields.footer,
          button2: {
            ...this.cardFields.footer.button2,
            onClick: () => {
              this.closeEmbeddedView.emit();
            },
          },
        },
      };
    }
    return this.cardFields;
  }
}
